"use client";

import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Factory, Truck, ShieldCheck, CircleCheck, Globe, Box, ClipboardCheck, ChartBar, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SupplyChainPillars() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const pillars = [{
    id: "procurement",
    label: "Procurement",
    icon: Factory,
    title: "Strategic Bulk Acquisition",
    description: "BRENDON J leverages direct manufacturer relationships to secure high-volume building supplies at wholesale rates. Our procurement strategy focuses on Australian standard compliance and stock reliability.",
    sop: ["Direct manufacturer vetting & quality auditing", "Strict compliance with AS/NZS building standards", "Volume-based pricing negotiation for B2B clients", "Inventory buffering for mission-critical materials"],
    stats: [{
      label: "Vetted Suppliers",
      value: "50+",
      icon: Globe
    }, {
      label: "AS/NZS Compliant",
      value: "100%",
      icon: CircleCheck
    }, {
      label: "Order Lead Time",
      value: "<24h",
      icon: Box
    }]
  }, {
    id: "logistics",
    label: "Logistics",
    icon: Truck,
    title: "Nationwide Freight Network",
    description: "We bridge the gap between wholesale storage and construction sites with a robust logistics framework. From Victoria to the furthest reaches of Australia, our distribution is seamless.",
    sop: ["Specialized heavy-load & bulk material transport", "Real-time GPS tracking for all interstate freight", "Integrated warehouse management systems", "Rapid response regional delivery coordination"],
    stats: [{
      label: "On-Time Fulfillment",
      value: "98.5%",
      icon: CircleCheck
    }, {
      label: "Service Area",
      value: "AUS",
      icon: Globe
    }, {
      label: "Fleet Availability",
      value: "24/7",
      icon: Truck
    }]
  }, {
    id: "delivery",
    label: "Site Delivery",
    icon: ShieldCheck,
    title: "Precision Site Placement",
    description: "Our delivery service goes beyond the curb. We coordinate specialized equipment to ensure materials like structural steel and bulk cement are placed exactly where your crew needs them.",
    sop: ["Crane & HIAB truck site-specific coordination", "Strict safety-first unloading protocols", "Digital proof of delivery (POD) archiving", "Site access assessment and route planning"],
    stats: [{
      label: "Damage Rate",
      value: "<0.05%",
      icon: ChartBar
    }, {
      label: "Safety Checks",
      value: "5-Step",
      icon: ClipboardCheck
    }, {
      label: "Insured Transit",
      value: "100%",
      icon: ShieldCheck
    }]
  }];
  return <section data-section-id="43371" id="our-support-model" ref={ref} className={`bg-background text-foreground py-20 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
        <div className="absolute top-10 right-10 w-64 h-64 border-2 border-primary rounded-full" />
        <div className="absolute bottom-20 left-20 w-96 h-96 border border-dashed border-secondary rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent/10 blur-3xl rounded-full" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-primary font-serif">
            Our Support <span className="text-amber-600">Model</span>
          </h2>
          <p className="text-muted-foreground text-lg font-sans">
            A comprehensive wholesale infrastructure designed to streamline building material procurement and precision delivery across Australia.
          </p>
        </div>

        <Tabs defaultValue="procurement" className="w-full max-w-6xl mx-auto">
          <div className="flex justify-center mb-12">
            <TabsList className="bg-muted/50 border border-border p-1 h-auto gap-2 rounded-md">
              {pillars.map((pillar, i) => <TabsTrigger key={pillar.id} value={pillar.id} data-index={i} className="rounded-sm px-8 py-3 text-base font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground shadow-sm transition-all duration-300 uppercase tracking-wider">
                  <pillar.icon className="w-4 h-4 mr-2" />
                  {pillar.label}
                </TabsTrigger>)}
            </TabsList>
          </div>

          {pillars.map((pillar, i) => <TabsContent key={pillar.id} value={pillar.id} className="mt-0 focus-visible:ring-0" data-index={i}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                
                {}
                <div className="space-y-8 animate-in fade-in slide-in-from-left-4 duration-500">
                  <div>
                    <h3 className="text-3xl md:text-4xl font-bold mb-4 text-primary tracking-tight">
                      {pillar.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-lg font-sans">
                      {pillar.description}
                    </p>
                  </div>

                  <div className="bg-muted/30 border border-border rounded-lg p-6 md:p-8">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-6 flex items-center gap-2 font-mono">
                      <ClipboardCheck className="w-4 h-4" />
                      Standard Operating Procedures
                    </h4>
                    <ul className="space-y-4">
                      {pillar.sop.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3">
                          <div className="mt-1.5 w-2 h-2 rounded-none bg-amber-500 shrink-0" />
                          <span className="text-foreground/90 text-sm md:text-base font-sans">{item}</span>
                        </li>)}
                    </ul>
                  </div>
                  
                  <div className="pt-4">
                    <Button asChild variant="outline" className="border-2 border-primary text-primary font-bold hover:bg-primary hover:text-primary-foreground rounded-none px-8 py-6">
                      <Link to="/ServicesPage">
                        View Service Details <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </div>

                {}
                <div className="animate-in fade-in slide-in-from-right-4 duration-500 delay-100">
                  <Card className="bg-white border-border shadow-xl overflow-hidden h-full rounded-none border-t-4 border-t-amber-500">
                    <CardHeader className="bg-muted/20 border-b border-border pb-6">
                      <div className="flex items-center justify-between">
                        <div className="p-3 bg-white rounded-none border border-border shadow-sm">
                          <pillar.icon className="w-8 h-8 text-amber-600" />
                        </div>
                        <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest bg-muted px-2 py-1">
                          SPEC_ID: {pillar.id.toUpperCase()}_BJ{i + 1}
                        </span>
                      </div>
                      <CardTitle className="mt-4 text-2xl font-bold tracking-tight">Operational Metrics</CardTitle>
                      <CardDescription className="font-sans">Real-time performance indicators for B2B orders</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="grid grid-cols-1 divide-y divide-border">
                        {pillar.stats.map((stat, idx) => <div key={idx} data-index={idx} className="p-6 flex items-center justify-between hover:bg-muted/10 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="p-2 rounded-none bg-muted text-secondary">
                                <stat.icon className="w-5 h-5" />
                              </div>
                              <span className="font-semibold text-muted-foreground uppercase text-sm tracking-wide">{stat.label}</span>
                            </div>
                            <span className="text-2xl font-bold text-primary tracking-tighter font-mono">{stat.value}</span>
                          </div>)}
                      </div>
                      <div className="p-6 bg-muted/20 border-t border-border">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono font-bold">
                          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                          NETWORK STATUS: ACTIVE_SYNC
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

              </div>
            </TabsContent>)}
        </Tabs>
      </div>
    </section>;
}